import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, firstValueFrom, map, of, shareReplay } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FeatureFlagService } from '../feature-flag/feature-flag.service';
import { TenantContextService } from './tenant-context.service';
import { returnUrlFor } from './return-url';

export interface LoginResponse {
  accessToken: string; refreshToken: string;
  accessTokenExpiresAt: string; refreshTokenExpiresAt: string;
  displayName: string; role: string;
}
export interface CurrentUser {
  email: string; displayName: string; role: string;
  tenantSlug: string; tenantDisplayName: string;
  /** Active people in this tenant, admins included. */
  memberCount: number;
  adminCount: number;
}

/**
 * The password rules a reset link will be judged against. The strings come
 * from the backend (`PasswordPolicy.Describe()`) rather than being restated
 * here, so a tenant that tightens its policy cannot end up with a form
 * promising the old rules.
 */
export interface PasswordResetChallenge {
  requirements: string[];
  minLength: number;
}

const ACCESS_KEY = 'esms.access_token';
const REFRESH_KEY = 'esms.refresh_token';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private tenant = inject(TenantContextService);
  private features = inject(FeatureFlagService);
  readonly accessToken = signal<string | null>(localStorage.getItem(ACCESS_KEY));
  readonly currentUser = signal<CurrentUser | null>(null);
  readonly isAuthenticated = computed(() => this.accessToken() !== null);
  // Mirrors the backend's role claim check. Reference catalogues are readable by
  // everyone; changing them, and the settings screens that do, are admin-only.
  readonly isAdmin = computed(() => this.currentUser()?.role === 'Admin');

  async login(tenantSlug: string, email: string, password: string): Promise<void> {
    const resp = await firstValueFrom(this.http.post<LoginResponse>(`${environment.apiBaseUrl}/auth/login`, { tenantSlug, email, password }));
    await this.startSession(tenantSlug, resp);
  }

  /**
   * Microsoft Entra ID sign-in. `entraIdToken` is the ID token from
   * `AzureAdService.acquireIdToken()`; the backend validates it against the
   * tenant's configured directory and answers with the same
   * `LoginResponseDto` the password flow returns, so the session it
   * establishes is indistinguishable from a password session afterwards.
   *
   * The request field is called `accessToken` because that is the name in
   * `AzureCallbackRequestDto` — the value it validates is the ID token.
   */
  async loginWithAzure(tenantSlug: string, entraIdToken: string): Promise<void> {
    const resp = await firstValueFrom(this.http.post<LoginResponse>(
      `${environment.apiBaseUrl}/auth/azure-callback`,
      { tenantSlug, accessToken: entraIdToken }
    ));
    await this.startSession(tenantSlug, resp);
  }

  /** Shared tail of both sign-in flows: persist tokens, pin the tenant, load /me. */
  private async startSession(tenantSlug: string, resp: LoginResponse): Promise<void> {
    localStorage.setItem(ACCESS_KEY, resp.accessToken);
    localStorage.setItem(REFRESH_KEY, resp.refreshToken);
    this.accessToken.set(resp.accessToken);
    this.tenant.set(tenantSlug);
    await this.loadCurrentUser();
  }

  async loadCurrentUser(): Promise<void> {
    if (!this.accessToken()) return;
    try {
      const me = await firstValueFrom(this.http.get<{
        user: { email: string; displayName: string; role: string };
        tenant: { slug: string; displayName: string; memberCount: number; adminCount: number };
      }>(`${environment.apiBaseUrl}/me`));
      this.currentUser.set({
        email: me.user.email, displayName: me.user.displayName, role: me.user.role,
        tenantSlug: me.tenant.slug, tenantDisplayName: me.tenant.displayName,
        memberCount: me.tenant.memberCount ?? 0, adminCount: me.tenant.adminCount ?? 0
      });
      // Fire and forget — FeatureFlagService swallows its own errors and the
      // signal updates reactively. Awaiting blocked login specs on un-mocked
      // /me/features. Guards that depend on flags should be reached only
      // after the signal populates (signal-based effects are reactive).
      void this.features.load();
    } catch { this.logout(); }
  }

  /**
   * Replace the signed-in user's own password.
   *
   * The session that made this call is dead the moment it succeeds — the
   * backend revokes every refresh token for this identity, in every tenant.
   * Callers must send the user back to sign-in rather than leave them holding
   * an access token that expires quietly in the next quarter of an hour.
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await firstValueFrom(this.http.post<void>(
      `${environment.apiBaseUrl}/auth/change-password`,
      { currentPassword, newPassword }
    ));
  }

  /** The password rules in force for the signed-in user's tenant. */
  passwordPolicy(): Promise<PasswordResetChallenge> {
    return firstValueFrom(this.http.get<PasswordResetChallenge>(
      `${environment.apiBaseUrl}/auth/password-policy`
    ));
  }

  /** Reads the password rules a reset link carries. Rejects if the link is dead. */
  describePasswordReset(token: string): Promise<PasswordResetChallenge> {
    return firstValueFrom(this.http.get<PasswordResetChallenge>(
      `${environment.apiBaseUrl}/auth/password-reset`,
      { params: { token } }
    ));
  }

  /** Spends a reset link. No session is established: the user signs in afterwards. */
  async redeemPasswordReset(token: string, newPassword: string): Promise<void> {
    await firstValueFrom(this.http.post<void>(
      `${environment.apiBaseUrl}/auth/password-reset`,
      { token, newPassword }
    ));
  }

  logout(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    this.accessToken.set(null);
    this.currentUser.set(null);
    this.tenant.clear();
    // Carry the page along. An access token lives fifteen minutes, so a session
    // lapsing mid-read is ordinary; landing on the home page afterwards loses
    // where they were on top of whatever they were doing.
    const back = returnUrlFor(this.router.url);
    if (back) {
      this.router.navigate(['/login'], { queryParams: { returnUrl: back } });
    } else {
      this.router.navigate(['/login']);
    }
  }

  getAccessTokenSnapshot(): string | null { return this.accessToken(); }

  /**
   * Exchanges the stored refresh token for a fresh pair, and emits the new
   * access token — or `null` when there is nothing to refresh with, or the
   * server refuses.
   *
   * <p>Concurrent callers share one request. This is not an optimisation: the
   * backend <b>rotates</b> the refresh token, so a second request would present
   * a value the first one already spent, and would kill the session it was
   * called to save. A screen that fires six requests at once — which every list
   * page does — would sign the user out reliably.</p>
   *
   * <p>Deliberately does not clear the session on failure. The caller decides
   * what a dead refresh means; the interceptor logs out, and a caller doing
   * something speculative might not want to.</p>
   */
  refreshSession(): Observable<string | null> {
    if (this.refreshInFlight) return this.refreshInFlight;

    const refreshToken = localStorage.getItem(REFRESH_KEY);
    const tenantSlug = this.tenant.slug();
    if (!refreshToken || !tenantSlug) return of(null);

    this.refreshInFlight = this.http
      .post<LoginResponse>(`${environment.apiBaseUrl}/auth/refresh`, { tenantSlug, refreshToken })
      .pipe(
        map(resp => {
          // Both halves are replaced. Keeping the old refresh token here is the
          // bug that makes the *second* expiry sign the user out.
          localStorage.setItem(ACCESS_KEY, resp.accessToken);
          localStorage.setItem(REFRESH_KEY, resp.refreshToken);
          this.accessToken.set(resp.accessToken);
          return resp.accessToken as string | null;
        }),
        catchError(() => of(null)),
        finalize(() => { this.refreshInFlight = null; }),
        shareReplay(1)
      );

    return this.refreshInFlight;
  }

  private refreshInFlight: Observable<string | null> | null = null;
}
